import { milestoneBadgeStatusMap } from '@/constants/milestones';
import { getAfterCompletionRedirectPath } from '@/helpers/getAfterCompletionRedirectPath';
import { resolveMilestonePackageRoute } from '@/helpers/milestonePackageRoute';
import usePermissions from '@/hooks/usePermissions';
import { useGetPerformProgressQuery } from '@/services/organizations';
import { PalmettoIcon } from '@palmetto/dsm-components';
import { Badge, Box, Button, Card, useBreakpoint } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  AccountMilestone,
  AccountRequirementDocument,
  AccountRequirementStatus,
  MilestonePackageRoute,
  MilestoneRequirement,
  MilestoneType,
  Packages,
  ProgramType,
  QueueDocumentResponse,
  QueueType,
  UserPermissions,
} from 'types';

import { useGetAccountQuery } from '../../services/accounts';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';

export const ActivationMilestonePackageContainer = () => {
  const { id } = useParams<{ id: any }>();
  const { data: account } = useGetAccountQuery(id);
  const { data: queuesData } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [QueueType.permissionToOperate],
  });
  const ptoQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.permissionToOperate);
  const { ptoQueue: usePtoQueue = false, ptoGreenlight = false, enableQcPriority = false } = useFlags();
  const { data: activationQueuesData } = useGetQueuesByAccountIdQuery(
    { accountId: id, types: [QueueType.activationPackage] },
    { skip: !id || !enableQcPriority },
  );
  const activationQueue = activationQueuesData?.find(
    (q: QueueDocumentResponse) => q.type === QueueType.activationPackage,
  );
  const targetDate = activationQueue?.reviewSla?.current?.targetDate;
  const decisionDate = activationQueue?.reviewSla?.current?.decisionDate;
  const formattedTarget = targetDate
    ? DateTime.fromISO(targetDate, { zone: 'utc' }).toLocal().toFormat("MMM d '·' h:mm a")
    : undefined;
  const navigate = useNavigate();
  const userPermissions = usePermissions();
  const { isPhone } = useBreakpoint();
  const { data: canPerformProgressBasedOnOrgStatus } = useGetPerformProgressQuery(id);

  const isSolarPR = account?.programType === ProgramType.solar && account?.address?.state?.toUpperCase() === 'PR';
  const isLightReachQCTech = userPermissions.includes(UserPermissions.lightReachQCTech);

  const handleClick = useCallback(() => {
    const programType = account?.programType;
    const route = resolveMilestonePackageRoute({
      accountId: id,
      programType,
      packageType: Packages.activationPackage,
      isSolarPR,
      hasPtoQueue: Boolean(ptoQueue),
      hasPtoFlagsEnabled: usePtoQueue && ptoGreenlight,
    });
    navigate(route.path, route.state ? { state: route.state } : undefined);
  }, [id, navigate, account, usePtoQueue, ptoGreenlight, ptoQueue, isSolarPR]);

  const activationPackageRequirement = useMemo(() => {
    if (!account) {
      return;
    }

    const activationMilestone = account.milestones?.find(
      (m: AccountMilestone) => m.type === MilestoneType.activation,
    );
    const activationPackageRequirement: AccountRequirementDocument = activationMilestone?.requirements.find(
      (req: AccountRequirementDocument) => req.type === MilestoneRequirement.activationPackage,
    );
    return activationPackageRequirement;
  }, [account]);

  const activationPackageStatus = activationPackageRequirement?.status ?? AccountRequirementStatus.pending;

  const isActivationPackageCreated = activationPackageStatus !== AccountRequirementStatus.pending;

  const handleAfterCompletionRedirect = () => {
    const routePath = getAfterCompletionRedirectPath(
      id,
      MilestonePackageRoute.activationPackage,
      isLightReachQCTech,
    );
    navigate(routePath);
  };
  // if all the documents exist in the completed group then the activation package is complete
  return (
    <Card margin="0 0 lg 0">
      <Box childGap="lg" padding="lg" alignItems="center" direction="row" justifyContent="space-between">
        <Box as="h3" fontWeight="medium" fontSize="md">
          Activation Package
        </Box>
        {!isActivationPackageCreated && (
          <Button
            as="button"
            disabled={!canPerformProgressBasedOnOrgStatus}
            navigate={handleClick}
            size="sm"
            variant="primary"
            tone="neutral"
            iconPrefix="add"
          >
            {!isPhone && 'Activation Package'}
          </Button>
        )}
      </Box>
      <Box
        borderWidth="xs 0 0 0"
        borderColor="separator"
        background="primary"
        padding={{ base: 'md 0', tablet: '0' }}
        direction="row"
        width="100"
        alignItems="center"
      >
        {isActivationPackageCreated ? (
          <>
            <Box width={{ base: '100', tablet: '60' }} direction={{ base: 'column', tablet: 'row' }}>
              <Box
                alignItems="flex-start"
                width={{ base: '100', tablet: '30' }}
                padding={{ base: 'md lg', tablet: 'md md md lg' }}
              >
                <Badge
                  message={milestoneBadgeStatusMap[activationPackageStatus].label}
                  variant={milestoneBadgeStatusMap[activationPackageStatus].variant}
                />
              </Box>
              <Box
                width={{ base: '100', tablet: '70' }}
                display="flex"
                childGap="xs"
                padding={{ base: '0 lg', tablet: 'md md md lg' }}
                alignItems="center"
              >
                {enableQcPriority &&
                formattedTarget &&
                !decisionDate &&
                (activationPackageStatus === AccountRequirementStatus.submitted ||
                  activationPackageStatus === AccountRequirementStatus.resubmitted) ? (
                  <Box direction="row" alignItems="center" childGap="2xs" fontSize="sm" color="body-secondary">
                    <PalmettoIcon name="clockSquare" size="xs" className="text-text-secondary" />
                    <Box as="span">Target review:</Box>
                    <Box as="span" fontWeight="bold" color="body-primary">
                      {formattedTarget}
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Box>
            <Box
              width="40"
              padding={{ base: '0 lg', tablet: 'md lg' }}
              direction={{ base: 'column', tablet: 'row' }}
              childGap="xs"
              justifyContent="flex-end"
              alignItems="flex-end"
            >
              {activationPackageStatus !== AccountRequirementStatus.completed && (
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  onClick={() => navigate(`activation-package/edit`)}
                >
                  Edit
                </Button>
              )}
              {activationPackageStatus === AccountRequirementStatus.completed && (
                <Button size="sm" variant="secondary" tone="neutral" onClick={handleAfterCompletionRedirect}>
                  View
                </Button>
              )}
            </Box>
          </>
        ) : null}
      </Box>
    </Card>
  );
};
